// The badge beside "Orders" on every page — and the new-order sound on the pages where the board is not open to make
// it (S6 6e). Reads only the counts (GET /api/ai/orders/counts): an arrival is the total growing, see orderAlerts.js.
import { useEffect, useRef, useState } from "react";
import { getOrderCounts } from "./ordersApi";
import { armSoundOnGesture, arrivalsBetween, markSeen, onSeen, playChime, readSeen, seenKey, soundWanted, totalOf, unseenOf } from "./orderAlerts";

// Often enough that an order is noticed from another page within half a minute; the board polls faster itself.
export const UNSEEN_POLL_MS = 30000;

export default function useUnseenOrders(userId, { enabled = true, boardOpen = false } = {}) {
  const [unseen, setUnseen] = useState(0);
  // The board makes its own sound while it is open; read through a ref so opening it does not restart the poll.
  const boardOpenRef = useRef(boardOpen);
  useEffect(() => { boardOpenRef.current = boardOpen; }, [boardOpen]);

  useEffect(() => {
    if (!enabled || userId == null) { setUnseen(0); return undefined; }
    let alive = true;
    let total = null;
    const refresh = () => { if (alive) setUnseen(unseenOf(total, readSeen(userId))); };
    const poll = async () => {
      try {
        const data = await getOrderCounts();
        if (!alive) return;
        const now = totalOf(data?.counts);
        const came = arrivalsBetween(total, now);
        total = now;
        // First sight in this browser, or orders taken away since: the baseline follows — nothing is "new".
        const seen = readSeen(userId);
        if (seen == null || seen > now) markSeen(userId, now);
        if (came > 0 && !boardOpenRef.current && soundWanted()) playChime();
        refresh();
      } catch { /* a dropped poll is not a badge */ }
    };
    poll();
    const id = setInterval(poll, UNSEEN_POLL_MS);
    const offSeen = onSeen(refresh);
    // Another tab of the same browser had the board in front of it.
    const onStorage = (e) => { if (e.key === seenKey(userId)) refresh(); };
    window.addEventListener("storage", onStorage);
    const offGesture = soundWanted() ? armSoundOnGesture() : () => {};
    return () => { alive = false; clearInterval(id); offSeen(); window.removeEventListener("storage", onStorage); offGesture(); };
  }, [userId, enabled]);

  return unseen;
}
